import { useQuery } from '@tanstack/preact-query';
import { useContext } from 'preact/hooks';
import { ApiClientContext } from '../arcticapi';
import type { Line } from '../arcticapi/types';
import { useTenantWebsiteBaseHref } from '../config/TenantContext';
import {
  mapLineToTimetableDetailsLine,
  type TimetableDetailsLine,
} from './timetableDetailsLine';

interface UseResolvedTimetableLineOptions {
  selectedLineId?: string | null;
  initialLine?: TimetableDetailsLine | null;
}

interface ResolvedTimetableLineState {
  line: TimetableDetailsLine | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Resolves a timetable details line from a stable identifier.
 *
 * Callers may provide `initialLine` to avoid jarring empty states while the
 * latest line details are loaded. The identifier remains the source of truth.
 */
export function useResolvedTimetableLine(
  options: UseResolvedTimetableLineOptions = {},
): ResolvedTimetableLineState {
  const client = useContext(ApiClientContext);
  const tenantWebsiteBaseHref = useTenantWebsiteBaseHref();
  const selectedLineId = options.selectedLineId?.trim() || null;

  const lineQuery = useQuery<Line, Error>({
    queryKey: ['timetable-line-details', selectedLineId],
    enabled: Boolean(client) && selectedLineId !== null,
    queryFn: ({ signal }) => {
      if (!client || !selectedLineId) {
        throw new Error(
          'Timetable details request is missing a line identifier.',
        );
      }

      return client.getLine(selectedLineId, { signal });
    },
  });

  const resolvedLine = lineQuery.data
    ? mapLineToTimetableDetailsLine(lineQuery.data, tenantWebsiteBaseHref)
    : (options.initialLine ?? null);

  return {
    line: resolvedLine,
    loading:
      Boolean(client) &&
      selectedLineId !== null &&
      lineQuery.isPending &&
      resolvedLine === null,
    error: resolvedLine === null ? lineQuery.error : null,
  };
}
